import {
  differenceInDays,
  format,
  isAfter,
  isEqual,
  startOfDay
} from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { Octicons } from '@expo/vector-icons';
import { WhereClause } from '@sierralabs/query-core';

import { mergeMedicationSurveyData } from '../../../../common/mergeMedicationSurveyData';
import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import RollingAverageChart, {
  RollingAverageDataSet
} from '../../../../components/charts/RollingAverageChart';
import IconButton from '../../../../components/IconButton';
import Spacer from '../../../../components/Spacer';
import { H5 } from '../../../../components/Typography';
import {
  StudyParticipantQueryParamsDto,
  useQueryStudyParticipantData,
  useQueryStudyParticipantDataLatest
} from '../../../../hooks/study-participant/useQueryStudyParticipantData';
import WebViewModal from '../../../modals/WebViewModal';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import Card from '../../../../components/Card';

const StyledView = styled(Card)`
  width: 100%;
  min-height: 96px;
  padding: 0 0 19px 0;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

const StyledH5 = styled(H5)`
  font-size: 21px;
  line-height: 24px;
  align-self: center;
`;

const StyledTitleContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding: 19px 19px 0 19px;
`;

export interface RollingAverageChartCardProps {
  title?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
  yAxisLabel?: string;
  withoutMedicationLabel?: string;
  withoutMedicationEntityId?: string;
  withoutMedicationColumnName?: string;
  withoutMedicationFilter?: WhereClause[];
  withMedicationLabel?: string;
  withMedicationEntityId?: string;
  withMedicationColumnName?: string;
  withMedicationFilter?: WhereClause[];
  goalEntityId?: string;
  goalColumnName?: string;
}

export const RollingAverageChartCard: React.FC<
  ScreenComponentProps<RollingAverageChartCardProps>
> = ({
  title,
  hasMoreInfo,
  infoTitle,
  infoHtml,
  yAxisLabel,
  withoutMedicationLabel,
  withoutMedicationEntityId,
  withoutMedicationColumnName,
  withoutMedicationFilter,
  withMedicationLabel,
  withMedicationEntityId,
  withMedicationColumnName,
  withMedicationFilter,
  goalEntityId,
  goalColumnName,
  studyParticipant,
  ...props
}) => {
  // get graph data
  const {
    queryResults: surveyResults,
    loadingQuery: loadingSurvey,
    loadingQueryError: loadingSurveyError,
    queryData: getSurveyData
  } = useQueryStudyParticipantData<any>(studyParticipant?.id || 0);
  useEffect(() => {
    if (studyParticipant?.id) {
      const params: StudyParticipantQueryParamsDto[] = [];
      if (withoutMedicationEntityId && withoutMedicationColumnName) {
        params.push({
          entityId: withoutMedicationEntityId,
          columnNames: [withoutMedicationColumnName],
          wheres: withoutMedicationFilter
        });
      }
      if (withMedicationEntityId && withMedicationColumnName) {
        params.push({
          entityId: withMedicationEntityId,
          columnNames: [withMedicationColumnName],
          wheres: withMedicationFilter
        });
      }
      getSurveyData(params);
    }
  }, [
    getSurveyData,
    studyParticipant?.id,
    withMedicationColumnName,
    withMedicationEntityId,
    withMedicationFilter,
    withoutMedicationColumnName,
    withoutMedicationEntityId,
    withoutMedicationFilter,
    goalEntityId,
    goalColumnName
  ]);
  useEffect(() => {
    if (studyParticipant?.id) {
      const params: StudyParticipantQueryParamsDto[] = [];
      if (withoutMedicationEntityId && withoutMedicationColumnName) {
        params.push({
          entityId: withoutMedicationEntityId,
          columnNames: [withoutMedicationColumnName],
          wheres: withoutMedicationFilter
        });
      }
      if (withMedicationEntityId && withMedicationColumnName) {
        params.push({
          entityId: withMedicationEntityId,
          columnNames: [withMedicationColumnName],
          wheres: withMedicationFilter
        });
      }
      getSurveyData(params);
    }
  }, [
    getSurveyData,
    studyParticipant?.id,
    withMedicationColumnName,
    withMedicationEntityId,
    withMedicationFilter,
    withoutMedicationColumnName,
    withoutMedicationEntityId,
    withoutMedicationFilter
  ]);
  // get goal data
  const {
    queryResults: goalResults,
    loadingQuery: loadingGoal,
    loadingQueryError: loadingGoalError,
    queryData: getGoal
  } = useQueryStudyParticipantDataLatest<any>(studyParticipant?.id || 0);
  useEffect(() => {
    if (studyParticipant?.id) {
      const params: StudyParticipantQueryParamsDto[] = [];
      if (goalEntityId && goalColumnName) {
        params.push({
          entityId: goalEntityId,
          columnNames: [goalColumnName]
        });
      }

      getGoal(params);
    }
  }, [getGoal, goalColumnName, goalEntityId, studyParticipant?.id]);

  const dataSets: RollingAverageDataSet[] = useMemo(() => {
    // make a copy of start date to prevent mutation
    const startDate = studyParticipant?.startDate
      ? new Date(studyParticipant.startDate)
      : undefined;
    if (!studyParticipant?.id)
      return [
        {
          caption: withoutMedicationLabel,
          chartData: [
            { x: 1, y: 5 },
            { x: 2, y: 6 },
            { x: 3, y: 4.5 },
            { x: 4, y: 6.5 },
            { x: 5, y: 5 },
            { x: 6, y: 6 },
            { x: 7, y: 5 },
            { x: 15, y: 5 },
            { x: 16, y: 6 },
            { x: 17, y: 4.5 },
            { x: 18, y: 6.5 },
            { x: 19, y: 5 },
            { x: 20, y: 6 },
            { x: 21, y: 5 }
          ]
        },
        {
          caption: withMedicationLabel,
          chartData: [
            { x: 8, y: 6 },
            { x: 9, y: 7 },
            { x: 10, y: 5.5 },
            { x: 11, y: 7 },
            { x: 12, y: 6.5 },
            { x: 13, y: 7 },
            { x: 14, y: 6 }
          ]
        }
      ];
    if (
      !surveyResults ||
      !startDate ||
      !withoutMedicationColumnName ||
      !withMedicationColumnName
    )
      return [];

    startDate.setHours(0, 0, 0, 0);
    const res = mergeMedicationSurveyData(surveyResults);
    // Extract and Merge all onMed data from the offMed (res[0]) data array into the onMed (res[1]) Data array
    // Check if there is offMed and onMed Query result
    if (res.length === 2) {
      res[1].forEach((data, index) => {
        // Get all offMed data that has the same intended start date from the onMed Data
        const duplicates = res[0].filter(o =>
          isEqual(o.intendedStartDate, data.intendedStartDate)
        );
        if (duplicates) {
          // Remove all offMed data that has the same intended start date from the onMed Data
          res[0] = res[0].filter(
            o => !isEqual(o.intendedStartDate, data.intendedStartDate)
          );
          // Update onMed data if data is not null and is more latest
          duplicates.forEach(d => {
            if (isEqual(d.intendedStartDate, data.intendedStartDate)) {
              Object.entries(d).forEach(([key, value]) => {
                if (typeof data[key] !== 'undefined' && data[key] !== null) {
                  if (
                    typeof value !== 'undefined' &&
                    value !== null &&
                    d.completedDate &&
                    data.completedDate &&
                    isAfter(d.completedDate, data.completedDate)
                  ) {
                    res[1][index][key] = value;
                  }
                } else {
                  res[1][index][key] = value;
                }
              });
            }
          });
        }
      });
    }
    return res?.map((data, index) => ({
      caption: index === 0 ? withoutMedicationLabel : withMedicationLabel,
      chartData: data
        ?.filter(row => {
          const value =
            index === 0
              ? row[withoutMedicationColumnName]
              : row[withMedicationColumnName];
          return value && !isNaN(value);
        })
        ?.map((row: any) => {
          row.intendedStartDate.setHours(0, 0, 0, 0);
          return {
            x: differenceInDays(row.intendedStartDate, startDate) + 1,
            y:
              index === 0
                ? row[withoutMedicationColumnName]
                : row[withMedicationColumnName]
          };
        })
    }));
  }, [
    surveyResults,
    studyParticipant?.startDate,
    studyParticipant?.id,
    withMedicationColumnName,
    withMedicationLabel,
    withoutMedicationColumnName,
    withoutMedicationLabel
  ]);
  const dateToday = startOfDay(new Date());
  const formattedStartDate = startOfDay(
    new Date(studyParticipant?.startDate || new Date())
  );
  const currentDay = differenceInDays(dateToday, formattedStartDate) + 1;
  const studyLength =
    studyParticipant?.studyLength || studyParticipant?.study?.studyLength || 24;
  const goal = goalResults?.[0]?.[
    goalColumnName as keyof typeof goalResults
  ] as number | undefined;
  const [showModal, setShowModal] = useState<boolean>();
  return (
    <StyledView>
      <StyledTitleContainer>
        {Boolean(title) && <StyledH5 color='dark'>{title}</StyledH5>}
        {hasMoreInfo && (
          <StyledIconButton onPress={() => setShowModal(true)}>
            <Octicons name='question' size={24} color='#494753' />
          </StyledIconButton>
        )}
      </StyledTitleContainer>
      <Spacer size={2} />
      {(surveyResults || !studyParticipant?.id) && (
        <RollingAverageChart
          label={yAxisLabel || 'Axis Label'}
          dataSets={dataSets}
          goal={
            goal
              ? isNaN(goal)
                ? undefined
                : goal
              : goalColumnName && !studyParticipant?.id
              ? 7
              : undefined
          }
          currentDay={currentDay}
          length={studyLength}
          {...props}
        />
      )}
      {loadingSurvey && <ActivityIndicatorScreen modal />}
      {loadingSurveyError && (
        <AlertBox fullWidth severity='error'>
          Chart error: {loadingSurveyError.message}
        </AlertBox>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledView>
  );
};

export const RollingAverageChartCardPlugin: ScreenComponentPlugin<RollingAverageChartCardProps> =
  {
    name: 'Rolling Average Chart Card',
    type: 'RollingAverageChartCard',
    categories: ['study-progress', 'my-chloe'],
    component: RollingAverageChartCard
  };
